import { FC } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import {
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  CopyPlus,
  MessageSquare,
  MoreVertical,
  Pencil,
  Star,
  Trash2,
} from 'lucide-react';

import { Table } from '@/components/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TruncateTextWithTooltip } from '@/components/ui/truncate-text-with-tooltip';
import { cn } from '@/lib/utils';
import { formatTimeAgo, safeFormatDate } from '@/utils/format-date';
import type { Agent, AgentSortField } from '@/types/agent-types';

interface AgentsTableProps {
  agents: Agent[];
  sortField: AgentSortField;
  sortDirection: 'asc' | 'desc';
  onSort: (field: AgentSortField) => void;
  duplicatingAgentId: string | null;
  settingPrimaryAgentId: string | null;
  onConfigure: (agent: Agent) => void;
  onChat: (agent: Agent) => void;
  onDuplicate: (agent: Agent) => void;
  onSetPrimary: (agent: Agent) => void;
  onDelete: (agent: Agent) => void;
}

export function agentWorkflowScopeCount(agent: Agent): number {
  return (
    (agent.workflow_allowlist_labels?.length ?? 0) +
    (agent.workflow_allowlist?.length ?? 0)
  );
}

function workflowScopeLabel(agent: Agent): string {
  const labelCount = agent.workflow_allowlist_labels?.length ?? 0;
  const allowCount = agent.workflow_allowlist?.length ?? 0;
  if (labelCount > 0) {
    return `${labelCount} label${labelCount === 1 ? '' : 's'}`;
  }
  if (allowCount > 0) {
    return `${allowCount} workflow${allowCount === 1 ? '' : 's'}`;
  }
  return '—';
}

export const AgentsTable: FC<AgentsTableProps> = ({
  agents,
  sortField,
  sortDirection,
  onSort,
  duplicatingAgentId,
  settingPrimaryAgentId,
  onConfigure,
  onChat,
  onDuplicate,
  onSetPrimary,
  onDelete,
}) => {
  const renderSortIcon = (columnField: AgentSortField) => {
    if (sortField !== columnField) {
      return <ChevronsUpDown className='size-4 text-gray-400' />;
    }
    if (sortDirection === 'asc') {
      return <ChevronUp className='size-4' />;
    }
    return <ChevronDown className='size-4' />;
  };

  const getAriaSort = (
    columnField: AgentSortField,
  ): 'ascending' | 'descending' | 'none' => {
    if (sortField !== columnField) {
      return 'none';
    }
    return sortDirection === 'asc' ? 'ascending' : 'descending';
  };

  const sortHeader = (field: AgentSortField, label: string) => (
    <button
      type='button'
      className='flex items-center gap-1 hover:text-gray-900 dark:hover:text-white transition-colors'
      onClick={() => onSort(field)}
      aria-label={`Sort by ${label.toLowerCase()}`}
      aria-sort={getAriaSort(field)}
    >
      {label}
      {renderSortIcon(field)}
    </button>
  );

  const columns: ColumnDef<Agent, unknown>[] = [
    {
      accessorKey: 'name',
      meta: { className: 'whitespace-normal py-3' },
      header: () => sortHeader('name', 'Name'),
      cell: ({ row }) => {
        const agent = row.original;
        return (
          <div className='flex flex-col gap-1.5'>
            <span className='flex items-center gap-2 font-semibold text-sm leading-none'>
              {agent.name}
              {agent.is_org_primary && (
                <Badge
                  variant='secondary'
                  className='bg-amber-500/10 text-amber-600 border-amber-500/20 dark:text-amber-400'
                  title="The org's primary chat agent"
                >
                  <Star className='mr-1 size-3' />
                  Primary
                </Badge>
              )}
            </span>
            {agent.description && (
              <TruncateTextWithTooltip
                text={agent.description}
                maxLines={2}
                className='text-sm text-muted-foreground leading-snug'
              />
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'exposed_via_a2a',
      header: () => sortHeader('exposed_via_a2a', 'Status'),
      cell: ({ row }) => {
        const agent = row.original;
        if (agent.exposed_via_a2a) {
          return (
            <Badge
              variant='secondary'
              className='bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:text-emerald-400'
              title='A2A protocol surface enabled'
            >
              <span className='mr-1 inline-block h-1.5 w-1.5 rounded-full bg-emerald-500' />
              A2A live
            </Badge>
          );
        }
        return (
          <Badge
            variant='secondary'
            className='text-muted-foreground'
            title='A2A protocol surface disabled'
          >
            A2A off
          </Badge>
        );
      },
      size: 90,
    },
    {
      accessorKey: 'model_name',
      header: () => sortHeader('model_name', 'Model'),
      cell: ({ row }) => {
        const agent = row.original;
        return (
          <Badge variant='outline' className='font-mono text-[10px]'>
            {agent.model_name || agent.model_provider || 'no model'}
          </Badge>
        );
      },
      size: 140,
    },
    {
      id: 'workflow_scope',
      header: () => sortHeader('workflow_scope', 'Workflows'),
      cell: ({ row }) => (
        <div className='text-sm dark:text-white'>
          {workflowScopeLabel(row.original)}
        </div>
      ),
      size: 100,
    },
    {
      accessorKey: 'created_at',
      header: () => sortHeader('created_at', 'Date Created'),
      cell: ({ row }) => (
        <div className='text-sm dark:text-white'>
          {safeFormatDate(new Date(row.original.created_at), 'MMMM d, yyyy')}
        </div>
      ),
      size: 110,
    },
    {
      accessorKey: 'updated_at',
      header: () => sortHeader('updated_at', 'Last Modified'),
      cell: ({ row }) => (
        <div
          className='text-sm dark:text-white'
          title={
            row.original.updated_at
              ? new Date(row.original.updated_at).toLocaleString()
              : undefined
          }
        >
          {row.original.updated_at
            ? formatTimeAgo(row.original.updated_at)
            : '—'}
        </div>
      ),
      size: 100,
    },
    {
      id: 'actions',
      header: '',
      meta: {
        className:
          'sticky right-0 z-10 min-w-12 bg-background overflow-visible [tr:hover_&]:bg-muted [tr[data-state=selected]_&]:bg-muted after:absolute after:top-0 after:bottom-0 after:-left-6 after:w-6 after:pointer-events-none after:shadow-none [[data-scroll=scrollable]_&]:after:shadow-[inset_-8px_0_8px_-8px_rgba(0,0,0,0.12)]',
      },
      cell: ({ row }) => {
        const agent = row.original;
        return (
          <div className='flex justify-end'>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant='ghost'
                  size='sm'
                  className='h-8 w-8 p-0'
                  aria-label={`Open options for ${agent.name}`}
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.stopPropagation();
                    }
                  }}
                >
                  <MoreVertical className='size-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align='end'
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              >
                <DropdownMenuItem onSelect={() => onChat(agent)}>
                  <MessageSquare className='size-4' />
                  Chat
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onConfigure(agent)}>
                  <Pencil className='size-4' />
                  Configure
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => onDuplicate(agent)}
                  disabled={duplicatingAgentId === agent.id}
                >
                  <CopyPlus className='size-4' />
                  {duplicatingAgentId === agent.id
                    ? 'Duplicating…'
                    : 'Duplicate'}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => onSetPrimary(agent)}
                  disabled={
                    settingPrimaryAgentId === agent.id || agent.is_org_primary
                  }
                >
                  <Star className='size-4' />
                  {agent.is_org_primary
                    ? 'Primary agent'
                    : settingPrimaryAgentId === agent.id
                      ? 'Setting as primary…'
                      : 'Set as primary'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => onDelete(agent)}>
                  <Trash2 className='size-4' />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  return (
    <Table
      columns={columns}
      data={agents}
      loading={false}
      onRowClick={(agent) => onConfigure(agent)}
      rowClassName={() => cn('group/row', 'cursor-pointer')}
    />
  );
};
